// frontend/src/components/SignUp.js

import React, { useState, useContext } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Button,
  TextField,
  Typography,
  CssBaseline,
  FormControl,
  FormLabel,
  IconButton,
  InputAdornment,
  Divider,
  Snackbar,
  Alert,
} from "@mui/material";
import { ThemeProvider, createTheme } from "@mui/material/styles";
import {
  Brightness4,
  Brightness7,
  Visibility,
  VisibilityOff,
} from "@mui/icons-material";
import { ThemeContext } from "../ThemeContext";
import API from "../API";

function SignUp() {
  const { darkMode, toggleTheme } = useContext(ThemeContext);
  const navigate = useNavigate();

  const [formData, setFormData] = useState({ username: "", password: "" });
  const [showPassword, setShowPassword] = useState(false);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState("error");

  const theme = createTheme({
    palette: {
      mode: darkMode ? "dark" : "light",
      background: {
        default: darkMode ? "#0d1117" : "#f9f9f9",
      },
      text: {
        primary: darkMode ? "#c9d1d9" : "#000000",
        secondary: darkMode ? "#8b949e" : "#5f6368",
      },
    },
    typography: {
      fontFamily: "'Roboto', sans-serif",
    },
  });

  const validatePassword = (password) => {
    const minLength = 8;
    const hasUppercase = /[A-ZА-ЯЁ]/.test(password);
    const hasLowercase = /[a-zа-яё]/.test(password);
    const hasDigit = /\d/.test(password);

    if (password.length < minLength) {
      return "Пароль должен содержать не менее 8 символов.";
    }
    if (!hasUppercase) {
      return "Пароль должен содержать хотя бы одну заглавную букву.";
    }
    if (!hasLowercase) {
      return "Пароль должен содержать хотя бы одну строчную букву.";
    }
    if (!hasDigit) {
      return "Пароль должен содержать хотя бы одну цифру.";
    }
    return null;
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    const passwordError = validatePassword(formData.password);

    if (!formData.username || !formData.password) {
      setSnackbarMessage("Пожалуйста, заполните все поля.");
      setSnackbarSeverity("error");
      setSnackbarOpen(true);
      return;
    }

    if (passwordError) {
      setSnackbarMessage(passwordError);
      setSnackbarSeverity("error");
      setSnackbarOpen(true);
      return;
    }

    try {
      await API.post("/auth/register", formData);
      setSnackbarMessage("Регистрация успешна!");
      setSnackbarSeverity("success");
      setSnackbarOpen(true);
      setTimeout(() => navigate("/"), 2000);
    } catch (error) {
      setSnackbarMessage(
        error.response?.data?.message || "Ошибка регистрации. Попробуйте снова."
      );
      setSnackbarSeverity("error");
      setSnackbarOpen(true);
    }
  };

  const handleTogglePassword = () => {
    setShowPassword(!showPassword);
  };

  const handleCloseSnackbar = () => {
    setSnackbarOpen(false);
  };

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "100vh",
          backgroundColor: theme.palette.background.default,
        }}
      >
        <Box
          sx={{
            width: "100%",
            maxWidth: "400px",
            height: "100vh",
            minHeight: "100vh",
            padding: "2rem",
            backgroundColor: darkMode ? "#161b22" : "#ffffff",
            boxShadow: darkMode
              ? "0 5px 20px rgba(0, 0, 0, 0.6)"
              : "0 5px 20px rgba(0, 0, 0, 0.1)",
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            alignItems: "center",
          }}
        >
          <Typography
            component="h1"
            variant="h5"
            align="center"
            sx={{
              fontWeight: "bold",
              marginBottom: "1rem",
              color: theme.palette.text.primary,
            }}
          >
            Sign Up
          </Typography>
          <Box
            component="form"
            noValidate
            autoComplete="on"
            onSubmit={handleSubmit}
            sx={{ display: "flex", flexDirection: "column", gap: 2 }}
          >
            <FormControl>
              <FormLabel
                htmlFor="username"
                sx={{ color: theme.palette.text.secondary }}
              >
                Username
              </FormLabel>
              <TextField
                id="username"
                type="text"
                name="username"
                autoComplete="username" // Можно оставить так или переименовать в email, если именно почта
                placeholder="Enter your username"
                fullWidth
                required
                value={formData.username}
                onChange={(e) =>
                  setFormData({ ...formData, username: e.target.value })
                }
                sx={{
                  input: {
                    backgroundColor: darkMode ? "#0d1117" : "#f9f9f9",
                    color: darkMode ? "#ffffff" : "#000000",
                    borderRadius: "15px",
                    padding: "10px",
                  },
                  ".MuiOutlinedInput-root": {
                    borderRadius: "15px",
                    backgroundColor: darkMode ? "#161b22" : "#ffffff",
                  },
                  fieldset: {
                    borderColor: darkMode ? "#8b949e" : "#cccccc",
                  },
                  "& .MuiOutlinedInput-root:hover fieldset": {
                    borderColor: "#58a6ff",
                  },
                  "& .MuiOutlinedInput-root.Mui-focused fieldset": {
                    borderColor: "#58a6ff",
                  },
                }}
              />
            </FormControl>
            <FormControl>
              <FormLabel
                htmlFor="password"
                sx={{ color: theme.palette.text.secondary }}
              >
                Password
              </FormLabel>
              <TextField
                id="password"
                type={showPassword ? "text" : "password"}
                name="password"
                autoComplete="new-password"
                placeholder="••••••"
                fullWidth
                required
                value={formData.password}
                onChange={(e) =>
                  setFormData({ ...formData, password: e.target.value })
                }
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={handleTogglePassword}
                        edge="end"
                        sx={{
                          backgroundColor: darkMode ? "#0d1117" : "#f9f9f9",
                          borderRadius: "50%",
                          padding: "8px",
                          "&:hover": {
                            backgroundColor: darkMode ? "#161b22" : "#e0e0e0",
                          },
                        }}
                      >
                        {showPassword ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
                sx={{
                  input: {
                    backgroundColor: darkMode ? "#0d1117" : "#f9f9f9",
                    color: darkMode ? "#ffffff" : "#000000",
                    borderRadius: "15px",
                    padding: "10px",
                  },
                  ".MuiOutlinedInput-root": {
                    borderRadius: "15px",
                    backgroundColor: darkMode ? "#161b22" : "#ffffff",
                  },
                  fieldset: {
                    borderColor: darkMode ? "#8b949e" : "#cccccc",
                  },
                  "& .MuiOutlinedInput-root:hover fieldset": {
                    borderColor: "#58a6ff",
                  },
                  "& .MuiOutlinedInput-root.Mui-focused fieldset": {
                    borderColor: "#58a6ff",
                  },
                }}
              />
            </FormControl>
            <Button
              type="submit"
              fullWidth
              variant="contained"
              sx={{
                backgroundColor: darkMode ? "#238636" : "#1976d2",
                color: "#ffffff",
                fontWeight: "bold",
                padding: "10px",
                borderRadius: "15px",
                "&:hover": {
                  backgroundColor: darkMode ? "#2ea043" : "#1565c0",
                },
              }}
            >
              Sign Up
            </Button>
            <Typography
              sx={{
                textAlign: "center",
                color: theme.palette.text.secondary,
                mt: 2,
              }}
            >
              Already have an account?{" "}
              <Button
                onClick={() => navigate("/")}
                sx={{
                  color: darkMode ? "#58a6ff" : "#0066cc",
                  textTransform: "none",
                  padding: 0,
                  minWidth: "auto",
                  "&:hover": { textDecoration: "underline" },
                }}
              >
                Sign in
              </Button>
            </Typography>
          </Box>
          <Divider>or</Divider>
        </Box>
      </Box>
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={4000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbarSeverity}>
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </ThemeProvider>
  );
}

export default SignUp;
